import {Component, inject, OnDestroy} from '@angular/core';
import {BaseComponent, WmsMessageComponent} from "@wms/components";
import {TabsModule} from "ngx-bootstrap/tabs";
import {ActivatedRoute, Router, RouterOutlet} from "@angular/router";
import { CommonModule } from "@angular/common";
import {isEmptyObject, noop, runtimeEventManager} from "@wm/core";
import {filter, forEach, isEmpty} from 'lodash-es';
import {PrefabConfigurationService, Tab, Config, Method, CONSTANTS, NormalizedMethod, Parameter, readFileByPath, writeFileByPath} from '@wms/settings/prefab-config';
import {IWMSSettingsSubWorkspace} from "@wms/settings";
import {FileService, NotificationService, PrefabService} from "@wms/services";
import {PageWsService} from "@wms/workspace";

@Component({
  selector: 'wms-prefab-configuration',
  templateUrl: './prefab-configuration.component.html',
  styleUrls: ['./prefab-configuration.component.less'],
  imports: [
    TabsModule,
    RouterOutlet,
    CommonModule,
    WmsMessageComponent
  ],
  standalone: true
})

export class PrefabConfigurationComponent extends BaseComponent implements IWMSSettingsSubWorkspace, OnDestroy {
  tabs : Tab[];
  selectedTab: Tab;
  prefabLifecycleEvents: string[] = ['Load', 'Destroy'];
  scripts: string[];
  properties: any[];
  events: any[];
  methods: any[];
  showErrorMsg: boolean;
  errorMsg: string;
  pageWsService: PageWsService;

  constructor(private router: Router, private prefabConfigService: PrefabConfigurationService,
              private activatedRoute: ActivatedRoute, private notificationService: NotificationService,
              private fileService: FileService, private prefabService: PrefabService) {
    super();
    this.pageWsService = inject(PageWsService);
    this.tabs = [
      { name: 'resources', title: this.locale.LABEL_FILES_TREE_RESOURCES, isActive: false },
      { name: 'properties', title: this.locale.LABEL_PROPERTY_PROPERTIES, isActive: false },
      { name: 'events', title: this.locale.LABEL_PROPERTY_EVENTS, isActive: false },
      { name: 'methods', title: this.locale.LABEL_PREFAB_PROPS_METHODS, isActive: false },
      { name: 'packaging', title: this.locale.LABEL_PREFAB_GROUP_PACKAGING, isActive: false },
      { name: 'appenv', title: this.locale.LABEL_TITLE_APP_ENVIRONMENT, isActive: false }
    ];
    let paths = this.router.url.split('?')[0].split('/'), path = paths.length > 3 ? paths[3] : 'resources', tab = filter(this.tabs, {name: path})[0];
    this.onTabSelect(tab);
    this.setPrefabConfig();
    this.showErrorMsg = this.prefabConfigService.showErrorMsg;
    this.errorMsg     = this.prefabConfigService.errorMsg;
    this.registerDestroyListener.add(this.prefabConfigService.hideErrorMsgObservable().subscribe(() => this.showErrorMsg = false));
    this.registerDestroyListener.add(this.prefabConfigService.iconUploadObservable().subscribe(() => this.savePrefabConfig()));
  }

  setPrefabConfig() {
    this.prefabConfigService.setConfig(JSON.parse(this.activatedRoute.snapshot.data['prefabConfig']));
  }

  isValid(): boolean {
      const hasPropertyValid = Object.values(this.prefabConfigService.prefabConfig.properties).every((property: any) => {
        return property['displayName'] && property['description'];
    });
   const hasInvalidConfig  = filter(this.prefabConfigService.isInvalid, (isInvalid) => {
      if(isInvalid) { return isInvalid;}
    }).length;
    return !hasInvalidConfig && hasPropertyValid
  }

  async prepareConfigForSave(): Promise<void> {
    await runtimeEventManager.emit('prepare-resources-save');
    await runtimeEventManager.emit('prepare-properties-save');
    await runtimeEventManager.emit('prepare-events-save');
    await runtimeEventManager.emit('prepare-methods-save');
    return ;
  }

  getMethodDefinition(name: string, args: any): string {
    return  name + ' = function (' + args + ')';
  }

  constructPrefabMethods(genMethodCode: string, name: string, args: any, comment: string) {
    let event = genMethodCode;
    event += '\n\n' + comment;
    event += '\n' + this.getMethodDefinition(name, args) + ' { \n\n';
    event += '};';
    return event;
  }

  addMethodsToPrefabScript(methods: any) {
    let prefix = 'Prefab.', pageName = CONSTANTS.MAIN_PAGE,
      fileLocalURL    = CONSTANTS.PAGE_DIRECTORY + pageName + '/' + pageName + '.js', nrmMethods: NormalizedMethod[] = [];
    // Normalizing methods data
    forEach(methods, (methodDetails: Method, methodName: string) => {
      let comments = methodDetails['description'] ? '/*\n' + ' * ' + methodDetails['description'] + '\n */' : '';
      // @ts-ignore
      let params = methodDetails.parameters.map((param: Parameter) => {
          if (param.name)
            return param.name;

        });
      nrmMethods.push({
        name      : prefix + methodName,
        params    : params.join(', '),
        comments  : comments
      });
    });

    // Reading script file to verify methods existance
    readFileByPath(fileLocalURL, (data: any) => {
      let methodsGenCode = '';
      forEach(nrmMethods, (method: NormalizedMethod) => {
        let mthdDef = this.getMethodDefinition(method.name, method.params);
        if (data.indexOf(method.name) === -1) {
          methodsGenCode    = this.constructPrefabMethods(methodsGenCode, method.name, method.params, method.comments);
        } else if (data.indexOf(mthdDef) === -1) {
          let mthdRegEx     = new RegExp('^' + method.name + '.*$', 'm');
          data              = data.replace(mthdRegEx, mthdDef + ' {');
        }
      });
      data += methodsGenCode;

      // Updating script content with new data
      this.pageWsService.saveFileContent(fileLocalURL, data).then(() => noop(), () => {
        this.notificationService.show('error', this.locale.MESSAGE_ERROR_TITLE, this.locale.MESSAGE_ERROR_FILE_WRITE_DESC);
      });

    }, () => {
      this.notificationService.show('error', this.locale.MESSAGE_ERROR_TITLE, this.locale.MESSAGE_ERROR_FILE_WRITE_DESC);
    });

  }
  savePrefabConfig(): Promise<void> {
    return new Promise((resolve, reject) => {
      let config: Config;
      try {
        this.prepareConfigForSave().then(() => {
          config = this.prefabConfigService.getConfig();

          this.prefabService.setConfig(config).then(() => {
            if(!isEmpty(config.methods)) {
              this.addMethodsToPrefabScript(config.methods);
            }
            this.prefabConfigService.isDirty = {};
            this.prefabConfigService.showErrorMsg = false;
            this.prefabConfigService.errorMsg = '';
            this.notificationService.show('success', this.locale.MESSAGE_SUCCESS_TITLE, this.locale.MESSAGE_SUCCESS_SAVE_PREFAB_SETTINGS);
            resolve();
          }, () => {
            this.notificationService.show('error', this.locale.MESSAGE_ERROR_TITLE, this.locale.MESSAGE_ERROR_PREFAB_PROPS_SAVE);
          });
        });
      } catch (error: any) {
        this.notificationService.show('error', this.locale.MESSAGE_ERROR_TITLE, this.locale.MESSAGE_ERROR_PREFAB_PROPS_SAVE);
      }
    });
  }

  async appEnvAndServerPropsSave(): Promise<void> {
    await runtimeEventManager.emit('update-appenv-profile');
    await runtimeEventManager.emit('save-server-props');
    return ;
  }

  saveChanges() {
    return new Promise((resolve, reject) => {
      runtimeEventManager.emit('set-prop-validity');
      if (!this.isValid()) {
        this.showErrorMsg = true;
        this.errorMsg     = this.prefabConfigService.errorMsg || this.locale.LABEL_STUDIO_SETTINGS_INVALID_INPUT;
        return;
      }
      this.showErrorMsg = false;

      /*Saving the prefab icon file only it is uploaded*/

      this.appEnvAndServerPropsSave().then(() => {
        if(this.prefabConfigService.prefabIconUpdated)
          runtimeEventManager.emit('upload-prefab-icon');

        else if(this.isWSDirty() && !this.prefabConfigService.prefabIconUpdated)
          resolve(this.savePrefabConfig());

        else
          this.prefabConfigService.isDirty = {};
      });
    });
  };


  discardChanges(otherSettingsClicked?: string) {
    this.prefabConfigService.isDirty = {};
    if(!otherSettingsClicked) {
      this.setPrefabConfig();
    }
    this.prefabConfigService.resetForm.next(false);
    this.prefabConfigService.errorMsg = '';
    this.prefabConfigService.showErrorMsg = false;
    this.showErrorMsg =false
  };

  isWSDirty() {
    return !isEmptyObject(this.prefabConfigService.isDirty);
  };

  onTabSelect(tab: Tab) {
    this.selectedTab = tab;
    this.tabs.map((tab: Tab) => tab.isActive = false);
    tab.isActive = true;
    this.router.navigate(['/settings/prefab-configuration/' + tab.name]);
  }

  getErrorMsg() {
    return this.errorMsg || this.prefabConfigService.errorMsg;
  }
  hasError() {
    return this.showErrorMsg || this.prefabConfigService.showErrorMsg
  }

  override ngOnDestroy() {
    this.prefabConfigService.showErrorMsg = false;
    this.prefabConfigService.errorMsg = '';
    this.registerDestroyListener.unsubscribe();
  }
}
